import { useState, type FormEvent } from "react";
import { useNavigate, useParams, useSearchParams, Navigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { getBrandingForSlug } from "@/lib/branding";
import { useAuth } from "@/lib/auth";

/**
 * Login do aluno — email + senha (criados pelo trainer). Autentica direto no
 * Supabase (signInWithPassword); o token vira sessão em localStorage. Mostra
 * logo/nome do trainer (branding público) antes do login, pra dar contexto.
 */
export function LoginView() {
  const { slug = "" } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { session, signIn } = useAuth();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const { data: branding } = useQuery({
    queryKey: ["branding", slug],
    queryFn: () => getBrandingForSlug(slug),
    staleTime: 5 * 60_000,
  });

  // Já logado? Volta pro destino original (ou home).
  if (session) {
    const next = searchParams.get("redirectedFrom") || `/s/${slug}`;
    return <Navigate to={next} replace />;
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await signIn(email.trim(), password);
      navigate(searchParams.get("redirectedFrom") || `/s/${slug}`, { replace: true });
    } catch {
      // Mensagem genérica de propósito — não revela se o email existe.
      setError("Email ou senha incorretos. Fale com seu treinador se precisar de ajuda.");
    } finally {
      setSubmitting(false);
    }
  }

  const trainerName = branding?.trainer.full_name?.trim();
  const logoUrl = branding?.trainer.logo_url || branding?.trainer.avatar_url;

  return (
    <div className="flex min-h-dvh flex-col items-center justify-center px-6 py-12">
      <div className="w-full max-w-sm animate-fade-in">
        <div className="mb-8 flex flex-col items-center gap-3 text-center">
          {logoUrl ? (
            <img
              src={logoUrl}
              alt={trainerName ?? "Treinador"}
              className="h-16 w-16 rounded-full object-cover ring-1 ring-border-strong"
            />
          ) : (
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-surface text-2xl font-display text-accent-soft">
              {(trainerName ?? "P").charAt(0).toUpperCase()}
            </div>
          )}
          <div>
            <h1 className="font-display text-xl text-ink">{trainerName ?? "Portal do Aluno"}</h1>
            <p className="mt-1 text-sm text-muted">Acesse seus treinos e dietas</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <label className="flex flex-col gap-1.5">
            <span className="text-xs font-medium text-muted">Email</span>
            <input
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="rounded-xl border border-border-subtle bg-surface px-4 py-3 text-ink outline-none transition-colors focus:border-accent-soft"
              placeholder="seu@email.com"
            />
          </label>

          <label className="flex flex-col gap-1.5">
            <span className="text-xs font-medium text-muted">Senha</span>
            <input
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="rounded-xl border border-border-subtle bg-surface px-4 py-3 text-ink outline-none transition-colors focus:border-accent-soft"
              placeholder="••••••••"
            />
          </label>

          {error ? (
            <p className="rounded-lg bg-danger/10 px-3 py-2 text-sm text-danger">{error}</p>
          ) : null}

          <button
            type="submit"
            disabled={submitting}
            className="mt-2 rounded-xl bg-accent px-4 py-3 font-medium text-ink transition-colors hover:bg-accent-hover disabled:opacity-60"
          >
            {submitting ? "Entrando…" : "Entrar"}
          </button>
        </form>

        <p className="mt-6 text-center text-xs text-faint">
          Esqueceu a senha? Peça ao seu treinador para redefinir.
        </p>
      </div>
    </div>
  );
}
